/**
 * Authentication Store
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getFirebaseAuth,
  googleProvider,
  microsoftProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User as FirebaseUser,
} from '@/plugins/firebase'
import { authApi } from '@/api'
import { triggerBackgroundSyncs } from '@/api/background-sync.api'
import apiClient from '@/api'
import type { AuthUser, LoginCredentials, RegisterData, UserRole } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const firebaseUser = ref<FirebaseUser | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  // Identifies whether the current session is a Mavriq team user or an external
  // ClientUser (Foysonis-style customer accessing their tenant via the portal).
  // 'team' = full Mavriq UI; 'client' = restricted client-facing UI.
  const userKind = ref<'team' | 'client' | null>(null)
  // Client branding (populated when a client user signs in)
  const clientLogoUrl = ref<string | null>(null)
  const clientPrimaryColor = ref<string | null>(null)
  const clientSecondaryColor = ref<string | null>(null)
  // Client tenant's vertical (e.g. 'coworking') — drives vertical-specific nav.
  const clientVertical = ref<string | null>(null)
  // For client users, this is their tenant/company they're scoped to. For team
  // users it's null (they pivot via WorkspaceContext instead).
  const clientCompanyId = ref<string | null>(null)
  const clientCompanyName = ref<string | null>(null)
  // ClientUser role: 'admin' | 'collaborator' | 'viewer'. Drives the read-only
  // banner and (for safety) lets the UI hide create/edit/delete buttons.
  // Always trust the backend — these flags are convenience, not security.
  const clientRole = ref<string | null>(null)
  const clientCanInviteUsers = ref<boolean>(false)
  // Path B: gates the PowerBI-style canvas builder for client portal users.
  // Mavriq team users always have it; clients get it only when an admin
  // toggles it on (rare — these are advanced-analyst tools).
  const clientCanUseCanvasBuilder = ref<boolean>(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userRole = computed(() => user.value?.role)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isManager = computed(() => ['admin', 'manager'].includes(user.value?.role || ''))
  const isClientUser = computed(() => userKind.value === 'client')
  const isTeamUser = computed(() => userKind.value === 'team')
  const isClientViewer = computed(() => isClientUser.value && clientRole.value === 'viewer')
  const isClientAdmin = computed(() => isClientUser.value && clientRole.value === 'admin')
  // Convenience capability flags. Team users always get true (their own /auth/me
  // role gates them elsewhere); client viewers are read-only.
  const canEdit = computed(() => isTeamUser.value || (isClientUser.value && clientRole.value !== 'viewer'))
  const canDelete = computed(() => canEdit.value)
  const canInvite = computed(() =>
    isTeamUser.value
    || (isClientUser.value && (clientRole.value === 'admin' || clientCanInviteUsers.value)),
  )
  // Path B: canvas builder access — team always, client only if flagged.
  const canUseCanvasBuilder = computed(() =>
    isTeamUser.value || (isClientUser.value && clientCanUseCanvasBuilder.value)
  )
  const fullName = computed(() =>
    user.value ? `${user.value.first_name} ${user.value.last_name}`.trim() : ''
  )

  // Check if user has required role
  function hasRole(roles: UserRole | UserRole[]): boolean {
    if (!user.value) return false
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(user.value.role)
  }

  /**
   * Resolve the current Firebase user to either a ClientUser (portal customer)
   * or a Mavriq team user. Tries the client endpoint first — if the token doesn't
   * map to a ClientUser, falls back to the team /auth/me path.
   *
   * Why client-first: a client token will 403 on /auth/me (no team record),
   * which would otherwise surface as a confusing "your account isn't set up" error.
   */
  async function resolveCurrentUser(): Promise<void> {
    // 1) Try client portal
    try {
      const res = await apiClient.get('/client-portal/me')
      const cu = res.data?.data
      if (cu && cu.id) {
        userKind.value = 'client'
        clientCompanyId.value = cu.company_id || null
        clientCompanyName.value = cu.company_name || null
        clientRole.value = cu.role || 'collaborator'
        clientCanInviteUsers.value = !!cu.can_invite_users
        clientCanUseCanvasBuilder.value = !!cu.can_use_canvas_builder
        user.value = {
          id: cu.id,
          email: cu.email,
          first_name: cu.first_name || '',
          last_name: cu.last_name || '',
          // Map client role to a team-shaped role string so existing hasRole
          // helpers don't crash; client routes already have their own permissions.
          role: 'client_user' as UserRole,
          avatar_url: cu.avatar_url || '',
          is_active: cu.is_active !== false,
        } as AuthUser
        // Load + apply white-label branding (best-effort; never blocks login)
        loadClientBranding().catch(() => {})
        return
      }
    } catch {
      // Not a client user (404 / 403 / etc.) — fall through to team path
    }

    // 2) Mavriq team path
    user.value = await authApi.getMe()
    userKind.value = 'team'
    clientCompanyId.value = null
    clientCompanyName.value = null
    clientRole.value = null
    clientCanUseCanvasBuilder.value = true  // team users implicitly have access
    clientCanInviteUsers.value = false
    // Clear any client branding that may have leaked in from a prior session
    clearClientBranding()
  }

  /**
   * Apply client branding to the document root. CSS variables are picked up
   * by AppHeader (logo + primary tint) and any component that opts into
   * `var(--client-primary-color)`. Safe to call on every change.
   */
  function applyClientBranding(branding: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }) {
    const root = document.documentElement
    clientLogoUrl.value = branding.logoUrl || null
    clientPrimaryColor.value = branding.primaryColor || null
    clientSecondaryColor.value = branding.secondaryColor || null
    if (branding.primaryColor) {
      root.style.setProperty('--client-primary-color', branding.primaryColor)
    } else {
      root.style.removeProperty('--client-primary-color')
    }
    if (branding.secondaryColor) {
      root.style.setProperty('--client-secondary-color', branding.secondaryColor)
    } else {
      root.style.removeProperty('--client-secondary-color')
    }
  }

  function clearClientBranding() {
    applyClientBranding({})
    clientVertical.value = null
  }

  async function loadClientBranding() {
    try {
      const res = await apiClient.get('/client-portal/integrations/branding')
      const d = res.data?.data || {}
      applyClientBranding({
        logoUrl: d.branding_logo_url,
        primaryColor: d.branding_primary_color,
        secondaryColor: d.branding_secondary_color,
      })
      clientVertical.value = d.vertical_type || null
    } catch {
      // No branding configured yet, or no permission — leave defaults
    }
  }

  // Initialize auth state listener
  async function initAuthListener(): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) {
      isLoading.value = false
      return
    }

    // Handle redirect result (for OAuth redirects)
    try {
      const result = await getRedirectResult(auth)
      if (result?.user) {
        user.value = await authApi.getMe()
      }
    } catch (err) {
      console.error('Error handling redirect result:', err)
    }

    onAuthStateChanged(auth, async (fbUser) => {
      firebaseUser.value = fbUser
      if (fbUser) {
        try {
          await resolveCurrentUser()
          // Trigger background syncs after successful auth (team users only)
          if (userKind.value === 'team') {
            triggerSyncsInBackground()
          }
        } catch (err) {
          console.error('Error fetching user:', err)
          user.value = null
          userKind.value = null
          clientCompanyId.value = null
          clientCompanyName.value = null
          clientRole.value = null
          clientCanInviteUsers.value = false
          clientCanUseCanvasBuilder.value = false
        }
      } else {
        user.value = null
        userKind.value = null
        clientCompanyId.value = null
        clientCompanyName.value = null
        clientRole.value = null
        clientCanInviteUsers.value = false
        clientCanUseCanvasBuilder.value = false
      }
      isLoading.value = false
    })

    // Check auth validity when user returns to the tab after being away
    // This helps catch expired sessions when the user has been idle
    setupVisibilityCheck(auth)
  }

  // Set up visibility change listener to check auth when user returns to tab
  function setupVisibilityCheck(auth: ReturnType<typeof getFirebaseAuth>): void {
    if (typeof document === 'undefined' || !auth) return

    let lastCheck = Date.now()
    const CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState !== 'visible') return

      // Only check if it's been more than 5 minutes since last check
      const now = Date.now()
      if (now - lastCheck < CHECK_INTERVAL) return
      lastCheck = now

      // If we don't have a firebase user, nothing to check
      if (!auth.currentUser) {
        if (user.value) {
          // We had a user but firebase session is gone - redirect to login
          console.log('[Auth] Session expired while away, redirecting to login...')
          user.value = null
          window.location.href = '/login'
        }
        return
      }

      // Try to get a fresh token - this will fail if the session is invalid
      try {
        await auth.currentUser.getIdToken(true)
        console.log('[Auth] Session still valid after returning to tab')
      } catch (err) {
        console.error('[Auth] Session invalid after returning to tab:', err)
        // Session is invalid - sign out and redirect
        try {
          await auth.signOut()
        } catch {
          // Ignore signout errors
        }
        user.value = null
        window.location.href = '/login'
      }
    })
  }

  // Sign in with email/password
  async function signInWithEmail(credentials: LoginCredentials): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    error.value = null
    isLoading.value = true

    try {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password)
      user.value = await authApi.getMe()
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      error.value = firebaseError.message || 'Failed to sign in'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Sign in with Google
  async function signInWithGoogle(): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    error.value = null
    isLoading.value = true

    try {
      console.log('Starting Google sign-in with popup...')
      // Try popup first
      const result = await signInWithPopup(auth, googleProvider)
      console.log('Popup sign-in successful:', result.user?.email)
      user.value = await authApi.getMe()
    } catch (err: unknown) {
      console.error('Google sign-in error:', err)
      const firebaseError = err as { code?: string; message?: string }
      console.error('Error code:', firebaseError.code)
      console.error('Error message:', firebaseError.message)
      // If popup was blocked or failed, fall back to redirect
      if (firebaseError.code === 'auth/popup-blocked' ||
          firebaseError.code === 'auth/popup-closed-by-user' ||
          firebaseError.code === 'auth/cancelled-popup-request') {
        console.log('Popup failed, trying redirect...')
        // Use redirect instead
        await signInWithRedirect(auth, googleProvider)
        return // User will be redirected, then handled by initAuthListener
      }
      error.value = firebaseError.message || 'Failed to sign in with Google'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Sign in with Microsoft
  async function signInWithMicrosoft(): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    error.value = null
    isLoading.value = true

    try {
      await signInWithPopup(auth, microsoftProvider)
      user.value = await authApi.getMe()
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      error.value = firebaseError.message || 'Failed to sign in with Microsoft'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Register new user
  async function register(data: RegisterData): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    error.value = null
    isLoading.value = true

    try {
      await createUserWithEmailAndPassword(auth, data.email, data.password)
      user.value = await authApi.register(data)
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      error.value = firebaseError.message || 'Failed to register'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Sign out
  async function signOut(): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    try {
      await firebaseSignOut(auth)
      user.value = null
      firebaseUser.value = null
      // Strip any client branding so the next sign-in starts clean (e.g. a
      // Mavriq admin on a shared machine doesn't see leftover Foysonis colors).
      clearClientBranding()
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      error.value = firebaseError.message || 'Failed to sign out'
      throw err
    }
  }

  // Reset password
  async function resetPassword(email: string): Promise<void> {
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase not initialized')

    error.value = null

    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      error.value = firebaseError.message || 'Failed to send reset email'
      throw err
    }
  }

  // Refresh user data — uses the same dual-path resolver so client users
  // stay client users after token refresh.
  async function refreshUser(): Promise<void> {
    if (!firebaseUser.value) return
    await resolveCurrentUser()
  }

  // Trigger background syncs (fire-and-forget, won't block UI)
  function triggerSyncsInBackground(): void {
    triggerBackgroundSyncs()
      .then((result) => {
        if (result.syncs_started.length > 0) {
          console.log('[Auth] Background syncs started:', result.syncs_started.join(', '))
        }
      })
      .catch((err) => {
        // Don't fail silently but don't throw - this is non-critical
        console.warn('[Auth] Background sync trigger failed:', err)
      })
  }

  return {
    // State
    user,
    firebaseUser,
    isLoading,
    error,
    userKind,
    clientCompanyId,
    clientCompanyName,
    clientLogoUrl,
    clientPrimaryColor,
    clientSecondaryColor,
    clientVertical,
    clientRole,
    clientCanInviteUsers,
    clientCanUseCanvasBuilder,
    // Getters
    isAuthenticated,
    userRole,
    isAdmin,
    isManager,
    isClientUser,
    isTeamUser,
    isClientViewer,
    isClientAdmin,
    canEdit,
    canDelete,
    canInvite,
    canUseCanvasBuilder,
    fullName,
    // Actions
    hasRole,
    initAuthListener,
    signInWithEmail,
    signInWithGoogle,
    signInWithMicrosoft,
    register,
    signOut,
    resetPassword,
    refreshUser,
    loadClientBranding,
    applyClientBranding,
  }
})
